import { config } from "@/config";
import { Army } from "@/domain/army";
import { getIllustrationSrc, getImageUploadSrc } from "@/domain/s3-urls";
import { UnitType } from "@/domain/unit-type";
import { stringifyFootprint, stringifyShortUnitType, stringifyUnitType } from "@/domain/units";
import { numericalStat } from "@/lib/numerical-stat";
import { useArmyOp } from "@/ui/army-provider/context";
import { UnitMiniIcons } from "@/ui/mini-icon";
import { DialogDisclosure } from "@ariakit/react";
import { AsyncOp, Failure, Success } from "@indietabletop/appkit";
import { useState } from "react";
import { KnownErrorMessage } from "../db-error";
import { DialogTrigger } from "../dialog-trigger";
import { DialogHeader } from "../form-dialog";
import { useEnsureShareLink } from "../hooks/use-ensure-share-link";
import { createUsePersistedState } from "../hooks/use-persisted-state";
import { Image } from "../image";
import { InfoDialog } from "../info-dialog";
import { Checkbox } from "../inputs/checkbox";
import { Link } from "../link";
import { LoadingIndicator } from "../loading-indicator";
import { LocalArmyProvider } from "../local-army-provider";
import { Paragraphs } from "../paragraphs";
import { button, interactiveText, paragraph, primaryTheme } from "../utils.css";
import * as css from "./style.css";

type ImageDimensions = {
  naturalWidth: number;
  naturalHeight: number;
};

const printWidthInInches = 7.5;
const printHeightInInches = 4;
const optimalPpi = 250;
const optimalPixelWidth = printWidthInInches * optimalPpi;
const optimalPixelHeight = printHeightInInches * optimalPpi;

function isBelowOptimalResolution({ naturalWidth, naturalHeight }: ImageDimensions) {
  return naturalWidth < optimalPixelWidth || naturalHeight < optimalPixelHeight;
}

function ArmyHeaderSection(props: {
  army: Army;
  includeArmyImage: boolean;
  includeArmyDescription: boolean;
  shareLinkOp: Success<string> | Failure<string>;
}) {
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(null);
  const { army, includeArmyImage, includeArmyDescription, shareLinkOp } = props;

  return (
    <div>
      {includeArmyImage && army.mainImage?.type === "IMAGE_UPLOAD" && (
        <div className={css.imageContainer}>
          <Image
            src={getImageUploadSrc(army.mainImage.src)}
            onResolvedSize={setImageDimensions}
            width={0}
            height={0}
            alt=""
            style={{
              display: "block",
              width: "100%",
              objectFit: "cover",
              aspectRatio: "16 / 9",
              height: "auto",
              marginBlockEnd: "2rem",
              borderRadius: "0.5rem",
            }}
          />

          {imageDimensions && isBelowOptimalResolution(imageDimensions) && (
            <DialogTrigger>
              <InfoDialog>
                <DialogHeader heading="Low Resolution" />

                <div style={{ textAlign: "left" }}>
                  <p className={paragraph}>
                    For best results, the army image should be at least {optimalPixelWidth}
                    &thinsp;×&thinsp;
                    {optimalPixelHeight}
                    px.
                  </p>

                  <p className={paragraph}>
                    The currently selected image is {imageDimensions.naturalWidth}&thinsp;×&thinsp;
                    {imageDimensions.naturalHeight}px. This may result in a blurry or pixelated
                    image when printed.
                  </p>

                  <p className={paragraph}>
                    Learn more in{" "}
                    <Link className={interactiveText} href="/help/printing-tips#images">
                      printing tips & tricks
                    </Link>
                    .
                  </p>
                </div>
              </InfoDialog>

              <DialogDisclosure className={css.dpiWarning}>⚠️ Low resolution</DialogDisclosure>
            </DialogTrigger>
          )}
        </div>
      )}

      <hgroup className={css.hGroup}>
        <div>
          {includeArmyImage && army.mainImage?.type === "ILLUSTRATION" && (
            <Image
              src={getIllustrationSrc(army.mainImage.id)}
              width={0}
              height={0}
              alt=""
              style={{ display: "block", width: "16rem", height: "auto", marginBlockEnd: "1.5rem" }}
            />
          )}
          <h1 className={css.title}>{army.name}</h1>
          <p className={css.armySummary}>
            {army.summary} · Hobgoblin {army.ruleset.versionName}
          </p>
          {includeArmyDescription && army.description && (
            <div className={css.armyDescription}>
              <Paragraphs
                text={army.description}
                className={css.armyDescriptionParagraph}
                richText={false}
              />
            </div>
          )}
        </div>
        {shareLinkOp.isSuccess ?
          <div style={{ marginInlineStart: "auto" }}>
            <img
              src={`${config.ITC_API_ORIGIN}/qr?${new URLSearchParams({ url: shareLinkOp.value })}`}
              alt=""
              width="64"
              height="64"
              style={{
                width: "5rem",
                height: "auto",
                aspectRatio: "1",
                borderRadius: "0.5rem",
                border: "1px solid hsl(0 0% 50%)",
              }}
            />
            <p
              style={{
                fontStyle: "italic",
                textAlign: "center",
                fontSize: "0.5em",
                marginTop: "1em",
              }}
            >
              Scan to load army in
              <br />
              Hobgoblin app
            </p>
          </div>
        : <div>
            <div
              style={{
                width: "5rem",
                height: "auto",
                aspectRatio: "1",
                fontSize: "0.625em",
                borderRadius: "0.5rem",
                border: "1px solid hsl(0 0% 90%)",
                padding: "0.25em",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
              }}
            >
              QR code not available
            </div>
            <p
              style={{
                fontStyle: "italic",
                textAlign: "center",
                fontSize: "0.5em",
                marginTop: "1em",
              }}
            >
              Connection error
            </p>
          </div>
        }
      </hgroup>
    </div>
  );
}

function UnitCount(props: { count: number }) {
  if (props.count > 1) {
    return <span style={{ color: "hsl(0 0% 50%)" }}> x{props.count}</span>;
  }

  return null;
}

function UnitsChart({ army }: { army: Army }) {
  return (
    <table className={css.table}>
      <thead>
        <tr className={css.row({ borderColor: "black" })}>
          <th
            className={css.cell({ fontWeight: "bold", align: "left" })}
            style={{ inlineSize: "40%" }}
          >
            Name
          </th>
          <th className={css.cell({ fontWeight: "bold" })} style={{ inlineSize: "14%" }}>
            Type
          </th>
          <th className={css.cell({ fontWeight: "bold" })}>Speed</th>
          <th className={css.cell({ fontWeight: "bold" })}>Range</th>
          <th className={css.cell({ fontWeight: "bold" })}>Strikes</th>
          <th className={css.cell({ fontWeight: "bold" })}>Courage</th>
          <th className={css.cell({ fontWeight: "bold" })}>Footprint</th>
          <th className={css.cell({ fontWeight: "bold" })}>Points</th>
        </tr>
      </thead>

      <tbody>
        {army.units.map((unit) => {
          return (
            <tr key={unit.id} className={css.row()}>
              <td className={css.cell({ fontSize: "large", align: "left" })}>
                <span className={css.unitName}>{unit.name}</span>
                <UnitCount count={unit.count} />
                <UnitMiniIcons keywords={unit.keywords} />
                {unit.keywordsSummary && (
                  <div className={css.unitKeywords}>{unit.keywordsSummary}</div>
                )}
              </td>
              <td className={css.cell()}>{stringifyUnitType(unit.type)}</td>
              <td className={css.cell({ fontSize: "large" })}>{numericalStat(unit.speed)}</td>
              <td className={css.cell({ fontSize: "large" })}>{numericalStat(unit.range, "″")}</td>
              <td className={css.cell({ fontSize: "large" })}>{unit.strikes}</td>
              <td className={css.cell({ fontSize: "large" })}>{numericalStat(unit.courage)}</td>
              <td className={css.cell()}>{stringifyFootprint(unit.footprint)}</td>
              <td className={css.cell({ fontSize: "large" })}>{unit.points}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function UnitsSection({ army }: { army: Army }) {
  return (
    <section className={css.section}>
      <h2 className={css.sectionHeading}>Units</h2>

      <UnitsChart army={army} />
    </section>
  );
}

function TargetNumbersChart({
  army,
  includeAllUnitTypeTargetNumbers,
}: {
  army: Army;
  includeAllUnitTypeTargetNumbers: boolean;
}) {
  const usedUnitTypes = new Set(army.units.map((unit) => unit.type));

  return (
    <table className={css.table}>
      <thead>
        <tr className={css.headerRow}>
          <th className={css.cell({ align: "left" })} style={{ inlineSize: "10%" }}>
            Unit Attacking
          </th>

          {army.ruleset.unitProfiles.map((unitProfile) => {
            return (
              <th key={unitProfile.type} className={css.cell()}>
                {stringifyUnitType(unitProfile.type)}
              </th>
            );
          })}
        </tr>
      </thead>

      <tbody>
        {army.ruleset.unitProfiles.map((unitProfile) => {
          if (!includeAllUnitTypeTargetNumbers && !usedUnitTypes.has(unitProfile.type)) {
            return null;
          }

          return (
            <tr key={unitProfile.type} className={css.row()}>
              <td className={css.cell({ align: "left", fontWeight: "bold" })}>
                {unitProfile.type === UnitType.MONSTROUS_INFANTRY ?
                  stringifyShortUnitType(unitProfile.type)
                : stringifyUnitType(unitProfile.type)}
              </td>

              {unitProfile.targetScores.map((score) => {
                return (
                  <td key={score.vsType} className={css.cell({ fontSize: "large" })}>
                    {score.minScore}+
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function TargetNumbersSection(props: { army: Army; includeAllUnitTypeTargetNumbers: boolean }) {
  const { army, includeAllUnitTypeTargetNumbers } = props;
  return (
    <section className={css.section} style={{ pageBreakInside: "avoid" }}>
      <h2 className={css.sectionHeading}>Target Numbers</h2>

      <TargetNumbersChart
        army={army}
        includeAllUnitTypeTargetNumbers={includeAllUnitTypeTargetNumbers}
      />
    </section>
  );
}

function KeywordsSection({ army }: { army: Army }) {
  return (
    <section className={css.section}>
      <h2 className={css.sectionHeading}>Keywords</h2>

      <div className={css.twoColumnSection}>
        {army.keywordsDefinitions.map((keyword) => {
          return (
            <div key={keyword.id} className={css.keywordDefinition}>
              <h3 className={css.entryHeading}>{keyword.name}</h3>
              <div
                className={css.richText}
                dangerouslySetInnerHTML={{
                  __html: keyword.descriptionWithTraits,
                }}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AbyssalAllegiancesSection({ army }: { army: Army }) {
  return (
    <section className={css.section}>
      <h2 className={css.sectionHeading}>Abyssal Allegiances</h2>

      <div className={css.twoColumnSection}>
        {army.abyssalAllegiancesDefinitions.map((allegiance) => {
          return (
            <div key={allegiance.id} className={css.keywordDefinition}>
              <h3 className={css.entryHeading}>{allegiance.name}</h3>

              {allegiance.spells.map((spell) => {
                return (
                  <div key={spell.name} style={{ marginTop: "1em" }}>
                    <h4 style={{ fontWeight: 600 }}>{spell.name}</h4>
                    <div
                      className={css.richText}
                      dangerouslySetInnerHTML={{
                        __html: spell.descriptionWithType,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CursedArtefactsSection({ army }: { army: Army }) {
  return (
    <section className={css.section}>
      <h2 className={css.sectionHeading}>Cursed Artefacts</h2>

      <div className={css.twoColumnSection}>
        {army.cursedArtefactsDefinitions.map((artefact) => {
          return (
            <div key={artefact.id} className={css.keywordDefinition}>
              <h3 className={css.entryHeading}>{artefact.name}</h3>
              <p>
                This artefact conveys the following keywords onto this unit:{" "}
                {artefact.keywordsSummary}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PrintArmyView(props: {
  army: Army;
  shareLinkOp: AsyncOp<string, string>;
  settings: {
    includeArmyImage: boolean;
    includeArmyDescription: boolean;
    includeKeywords: boolean;
    includeAbyssalAllegiances: boolean;
    includeCursedArtefacts: boolean;
    includeTargetNumbers: boolean;
    includeAllUnitTypeTargetNumbers: boolean;
    highContrast: { checked: boolean };
    minFontSize: { checked: boolean };
  };
}) {
  const { army, shareLinkOp, settings } = props;

  if (shareLinkOp.isPending) {
    return (
      <div className={css.prepMessageContainer}>
        <LoadingIndicator />
        <p>Preparing for print</p>
      </div>
    );
  }

  return (
    <main
      className={css.printContainer({
        highContast: settings.highContrast.checked,
        minSize: settings.minFontSize.checked,
      })}
    >
      <ArmyHeaderSection
        army={army}
        includeArmyDescription={settings.includeArmyDescription}
        includeArmyImage={settings.includeArmyImage}
        shareLinkOp={shareLinkOp}
      />

      <UnitsSection army={army} />

      {settings.includeTargetNumbers && (
        <TargetNumbersSection
          army={army}
          includeAllUnitTypeTargetNumbers={settings.includeAllUnitTypeTargetNumbers}
        />
      )}

      {settings.includeKeywords && army.keywordsDefinitions.length > 0 && (
        <KeywordsSection army={army} />
      )}

      {settings.includeAbyssalAllegiances && army.abyssalAllegiancesDefinitions.length > 0 && (
        <AbyssalAllegiancesSection army={army} />
      )}

      {settings.includeCursedArtefacts && army.cursedArtefactsDefinitions.length > 0 && (
        <CursedArtefactsSection army={army} />
      )}
    </main>
  );
}

const usePrintSettings = createUsePersistedState(
  {
    // Sections
    include_army_image: true,
    include_army_description: true,
    include_targets_numbers: true,
    include_all_unit_type_target_numbers: false,
    include_keywords: true,
    include_abyssal_allegiances: true,
    include_cursed_artefacts: false,

    // Accessibility
    high_contrast_mode: false,
    min_font_size: false,
  },
  { namespace: "print:" },
);

function useCheckbox(...args: Parameters<typeof usePrintSettings>) {
  const [checked, setChecked] = usePrintSettings(...args);

  return {
    name: args[0],
    checked: checked,
    onChecked: () => setChecked(true),
    onUnchecked: () => setChecked(false),
  };
}

function PrintArmyContent(props: { army: Army }) {
  const [includeArmyImage, setIncludeArmyImage] = usePrintSettings("include_army_image");
  const [includeArmyDescription, setIncludeArmyDescription] = usePrintSettings(
    "include_army_description",
  );
  const [includeKeywords, setIncludeKeywords] = usePrintSettings("include_keywords");
  const [includeAbyssalAllegiances, setIncludeAbyssalAllegiances] = usePrintSettings(
    "include_abyssal_allegiances",
  );
  const [includeCursedArtefacts, setIncludeCursedArtefacts] = usePrintSettings(
    "include_cursed_artefacts",
  );
  const [includeTargetNumbers, setIncludeTargetNumbers] =
    usePrintSettings("include_targets_numbers");

  const [includeAllUnitTypeTargetNumbers, setincludeAllUnitTypeTargetNumbers] = usePrintSettings(
    "include_all_unit_type_target_numbers",
  );

  const highContrast = useCheckbox("high_contrast_mode");
  const minFontSize = useCheckbox("min_font_size");

  const shareLinkOp = useEnsureShareLink(props.army.data);

  return (
    <div className={css.printPage}>
      <div className={css.printPagePreview}>
        <PrintArmyView
          {...props}
          shareLinkOp={shareLinkOp}
          settings={{
            includeArmyImage,
            includeArmyDescription,
            includeKeywords,
            includeAbyssalAllegiances,
            includeCursedArtefacts,
            includeTargetNumbers,
            includeAllUnitTypeTargetNumbers,
            highContrast,
            minFontSize,
          }}
        />
      </div>

      <div className={css.sidenav.container}>
        <div className={css.sidenav.header}>
          <div className={css.sidenav.heading}>Print</div>

          <Link href={`/army/a/${props.army.id}`}>
            <Image src="/icons/cross.svg" alt="Back" width={40} height={40} />
          </Link>
        </div>

        <div className={css.sidenav.content}>
          <section>
            <p className={paragraph}>
              To get the best results when printing, check out the{" "}
              <Link className={interactiveText} href="/help/printing-tips">
                printing tips & tricks
              </Link>
              .
            </p>
          </section>

          <section>
            <h2 className={css.sidenav.subheading}>Sections</h2>
            <p>Which sections do you want to print?</p>

            <div className={css.checkboxes}>
              <Checkbox
                label="Army image"
                name="include_army_image"
                checked={includeArmyImage}
                onChecked={() => setIncludeArmyImage(true)}
                onUnchecked={() => setIncludeArmyImage(false)}
              />
              <Checkbox
                label="Army description"
                name="include_army_description"
                checked={includeArmyDescription}
                onChecked={() => setIncludeArmyDescription(true)}
                onUnchecked={() => setIncludeArmyDescription(false)}
              />
              <Checkbox
                label="Target numbers"
                name="include_targets_numbers"
                checked={includeTargetNumbers}
                onChecked={() => setIncludeTargetNumbers(true)}
                onUnchecked={() => setIncludeTargetNumbers(false)}
              />
              {includeTargetNumbers && (
                <div
                  style={{
                    paddingInlineStart: "0.5rem",
                    borderInlineStart: "2px solid hsl(0 0% 90%)",
                  }}
                >
                  <Checkbox
                    label="Include all unit types"
                    name="include_all_unit_type_target_numbers"
                    checked={includeAllUnitTypeTargetNumbers}
                    onChecked={() => setincludeAllUnitTypeTargetNumbers(true)}
                    onUnchecked={() => setincludeAllUnitTypeTargetNumbers(false)}
                  />
                </div>
              )}
              <Checkbox
                label="Keywords"
                name="include_keywords"
                checked={includeKeywords}
                onChecked={() => setIncludeKeywords(true)}
                onUnchecked={() => setIncludeKeywords(false)}
              />
              <Checkbox
                label="Abyssal allegiances"
                name="include_abyssal_allegiances"
                checked={includeAbyssalAllegiances}
                onChecked={() => setIncludeAbyssalAllegiances(true)}
                onUnchecked={() => setIncludeAbyssalAllegiances(false)}
              />
              <Checkbox
                label="Cursed artefacts"
                name="include_cursed_artefacts"
                checked={includeCursedArtefacts}
                onChecked={() => setIncludeCursedArtefacts(true)}
                onUnchecked={() => setIncludeCursedArtefacts(false)}
              />
            </div>
          </section>

          <section>
            <h2 className={css.sidenav.subheading}>Accessibility</h2>
            <p>Adjust print output for ease of reading.</p>

            <div className={css.checkboxes}>
              <Checkbox {...highContrast} label="High contrast mode" />
              <Checkbox {...minFontSize} label="Larger minimum font size" />
            </div>
          </section>

          <div className={css.sidenav.printButtonContainer}>
            <button className={button} style={primaryTheme} onClick={() => window.print()}>
              Print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrintArmyMain() {
  const armyOp = useArmyOp();

  if (armyOp.isPending) {
    return <LoadingIndicator />;
  }

  if (armyOp.isFailure) {
    return <KnownErrorMessage error={armyOp.failure} />;
  }

  return <PrintArmyContent army={armyOp.value} />;
}

export function PrintArmyPage(props: { params: { armyId: string } }) {
  return (
    <LocalArmyProvider armyId={props.params.armyId}>
      <PrintArmyMain />
    </LocalArmyProvider>
  );
}
